import { useEffect, useState } from 'react';
import type { RecruitmentTransport } from '@rudi/shared/types/recruitment';
import { Button } from './button';

type Metrics = {
  mediaSpendCents: number | null;
  impressions: number | null;
  clicks: number | null;
  clickThroughRate: number | null;
  costPerClickCents: number | null;
  deliveredContactReferrals: number;
  costPerDeliveredReferralCents: number | null;
};
type Report = {
  campaignId: string;
  name: string;
  deliveryState: string;
  siteScope: string;
  totals: Metrics;
  channels: (Metrics & { channel: string })[];
  creatives: (Metrics & { creativeReference: string })[];
  sites: { siteId: string; siteName?: string | null; deliveredContactReferrals: number }[];
  finance: {
    managementFeeCents: number | null;
    customerLiabilityCents: number | null;
    remainingBudgetCents: number | null;
    recordedProviderChargesCents: number | null;
    reconciledProviderCents: number | null;
    absorbedMediaCents: number | null;
  };
  sources: {
    channel: string;
    spendAsOf: string | null;
    metricsAsOf: string | null;
    spendComplete: boolean;
    metricsComplete: boolean;
    stale: boolean;
    reportingErrorCode?: string | null;
  }[];
};
type Reports = { campaigns: Report[]; combined: Metrics };
const unknown = 'Nicht verfügbar';
const number = (value: number | null) =>
  value === null
    ? unknown
    : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value);
const money = (value: number | null) =>
  value === null
    ? unknown
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value / 100);
const percent = (value: number | null) =>
  value === null
    ? unknown
    : new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 2 }).format(value);
const timestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString('de-DE') : unknown;
const channels: Record<string, string> = {
  meta: 'Facebook / Instagram',
  google_search: 'Google Search',
};
const states: Record<string, string> = {
  disabled: 'Nicht gestartet',
  delivering: 'Wird ausgeliefert',
  partial_delivery: 'Teilweise Auslieferung',
  launch_requested: 'Start angefordert',
  provider_review: 'Prüfung beim Provider',
  creation_unknown: 'Provider-Antwort unklar',
  rejected: 'Vom Provider abgelehnt',
  partial_failure: 'Kanalfehler',
  pause_pending: 'Pause angefordert',
  paused: 'Pause bestätigt',
  cancelled: 'Abgebrochen',
};

export default function RecruitmentReporting({ transport }: { transport: RecruitmentTransport }) {
  const api = transport.funding;
  const [data, setData] = useState<Reports | null>(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState('');
  const [level, setLevel] = useState('channels');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!api) return;
    let active = true;
    setData(null);
    setError(false);
    const load = async () => {
      try {
        const result = await api.request<Reports>('/reporting');
        if (!Array.isArray(result?.campaigns) || !result.combined)
          throw new Error('Reporting response is unavailable.');
        if (active) {
          setData(result);
          setError(false);
        }
      } catch {
        if (active) {
          setData(null);
          setError(true);
        }
      }
    };
    void load();
    const timer = setInterval(() => void load(), 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [api, reload]);
  if (!api) return null;
  const report = data?.campaigns.find(row => row.campaignId === selected);
  const totals = report?.totals ?? data?.combined;
  const sources = (report ? [report] : (data?.campaigns ?? [])).flatMap(campaign =>
    campaign.sources.map((source, index) => ({
      ...source,
      key: `${campaign.campaignId}-${index}`,
      campaignName: campaign.name,
    }))
  );
  const rows = report
    ? level === 'channels'
      ? report.channels.map(row => ({ ...row, label: channels[row.channel] ?? row.channel }))
      : report.creatives.map(row => ({ ...row, label: row.creativeReference }))
    : (data?.campaigns.map(row => ({ ...row.totals, label: row.name })) ?? []);
  return (
    <section aria-label="Kampagnenleistung" className="min-w-0 space-y-4 rounded-3xl border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Kampagnenleistung</h2>
        <Button variant="outline" onClick={() => setReload(value => value + 1)}>
          Bericht neu laden
        </Button>
      </div>
      {error ? (
        <p role="alert">Bericht konnte nicht geladen werden. Bitte erneut laden.</p>
      ) : !data ? (
        <p role="status">Bericht wird geladen…</p>
      ) : (
        <>
          {data.campaigns.length === 0 ? (
            <p>Keine Kampagnen mit Berichtsberechtigung.</p>
          ) : (
            <>
              <label className="block">
                Kampagne vergleichen
                <select
                  className="mt-1 block w-full rounded-3xl border bg-background p-2"
                  value={selected}
                  onChange={event => setSelected(event.target.value)}
                >
                  <option value="">Alle berechtigten Kampagnen</option>
                  {data.campaigns.map(row => (
                    <option key={row.campaignId} value={row.campaignId}>
                      {row.name}
                    </option>
                  ))}
                </select>
              </label>
              {report && (
                <>
                  <h3 className="font-semibold">{report.name}</h3>
                  <p>{states[report.deliveryState] ?? report.deliveryState}</p>
                </>
              )}
              {totals && (
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    ['Mediakosten, vorläufig', money(totals.mediaSpendCents)],
                    ['Impressionen', number(totals.impressions)],
                    ['Klicks', number(totals.clicks)],
                    ['Klickrate', percent(totals.clickThroughRate)],
                    ['Kosten je Klick', money(totals.costPerClickCents)],
                    ['Zugestellte Kontaktvermittlungen', number(totals.deliveredContactReferrals)],
                    ['Mediakosten je Vermittlung', money(totals.costPerDeliveredReferralCents)],
                    ...(report
                      ? [
                          ['Managementgebühr, vorläufig', money(report.finance.managementFeeCents)],
                          [
                            'Kundenbelastung, vorläufig',
                            money(report.finance.customerLiabilityCents),
                          ],
                          ['Restbudget, vorläufig', money(report.finance.remainingBudgetCents)],
                          [
                            'Erfasste Providerbelastungen nach Gutschriften',
                            money(report.finance.recordedProviderChargesCents),
                          ],
                          [
                            'Abschließend abgeglichene Mediakosten',
                            money(report.finance.reconciledProviderCents),
                          ],
                          [
                            'Von Ivelion getragene Mediakosten, vorläufig',
                            money(report.finance.absorbedMediaCents),
                          ],
                        ]
                      : []),
                  ].map(([label, value]) => (
                    <div key={label} className="min-w-0 rounded-3xl bg-muted p-3">
                      <dt className="text-sm text-muted-foreground">{label}</dt>
                      <dd className="break-words text-lg font-semibold">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <p className="text-sm text-muted-foreground">
                Alle Geldbeträge in EUR vor Steuern. Die Managementgebühr beträgt 30 % der
                autorisierten Mediakosten. Providerbelastungen können noch unvollständig sein. Die
                endgültige Abrechnung steht aus.
              </p>
              {sources.some(
                source =>
                  source.stale ||
                  !source.spendComplete ||
                  !source.metricsComplete ||
                  source.reportingErrorCode
              ) && <p role="status">Daten veraltet oder unvollständig.</p>}
              <ul aria-label="Datenquellen" className="space-y-2 text-sm">
                {sources.map(source => (
                  <li key={source.key}>
                    {!report && `${source.campaignName}: `}
                    {channels[source.channel] ?? source.channel}: Kostenstand{' '}
                    {timestamp(source.spendAsOf)}, Leistungsstand {timestamp(source.metricsAsOf)}.
                    Kosten {source.spendComplete ? 'vollständig' : 'unvollständig'}, Leistung{' '}
                    {source.metricsComplete ? 'vollständig' : 'unvollständig'}.
                    {source.stale && ' Stand älter als 24 Stunden oder unbekannt.'}
                  </li>
                ))}
              </ul>
              {report && (
                <>
                  <label className="block">
                    Vergleichsebene
                    <select
                      className="mt-1 block w-full rounded-3xl border bg-background p-2"
                      value={level}
                      onChange={event => setLevel(event.target.value)}
                    >
                      <option value="channels">Kanäle</option>
                      <option value="creatives">Motive</option>
                    </select>
                  </label>
                </>
              )}
              <div
                className="overflow-x-auto rounded-3xl border"
                tabIndex={0}
                role="region"
                aria-label="Leistungsvergleich"
              >
                <table className="w-full text-left text-sm">
                  <caption className="p-3 text-left">
                    {report
                      ? level === 'channels'
                        ? 'Kanalvergleich'
                        : 'Motivvergleich'
                      : 'Kampagnenvergleich'}
                  </caption>
                  <thead>
                    <tr>
                      {[
                        'Name',
                        'Mediakosten',
                        'Impressionen',
                        'Klicks',
                        'Klickrate',
                        'Kosten je Klick',
                        'Vermittlungen',
                        'Kosten je Vermittlung',
                      ].map(label => (
                        <th key={label} scope="col" className="p-3">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.label} className="border-t">
                        <th scope="row" className="max-w-64 break-words p-3">
                          {row.label}
                        </th>
                        {[
                          money(row.mediaSpendCents),
                          number(row.impressions),
                          number(row.clicks),
                          percent(row.clickThroughRate),
                          money(row.costPerClickCents),
                          number(row.deliveredContactReferrals),
                          money(row.costPerDeliveredReferralCents),
                        ].map((value, index) => (
                          <td key={index} className="p-3">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm">
                Personen zählen einmal je Kampagne. Eine Person kann in mehreren Kanal-, Motiv- oder
                Prüfstellenzeilen erscheinen. Diese Zeilen sind nicht addierbar. Über mehrere
                Kampagnen summieren wir Kampagnenergebnisse, keine kampagnenübergreifend eindeutigen
                Personen.
              </p>
              <p className="text-sm">
                Mediakosten werden gemeinsam eingesetzt. Keine exakten Kosten je Prüfstelle. Ohne
                Providerdaten je Motiv bleiben dessen Mediakosten und Leistungswerte nicht
                verfügbar. Die Ergebnisse zeigen keine statistisch gesicherten Gewinner.
              </p>
              {report && (
                <div>
                  <h3 className="font-semibold">
                    {report.siteScope === 'own'
                      ? 'Ergebnisse Ihrer Prüfstellen'
                      : 'Ergebnisse der teilnehmenden Prüfstellen'}
                  </h3>
                  <ul className="mt-2 space-y-2">
                    {report.sites.map(site => (
                      <li key={site.siteId} className="break-words rounded-3xl bg-muted p-3">
                        <span className="block break-words">{site.siteName || site.siteId}</span>
                        {number(site.deliveredContactReferrals)} zugestellte Kontaktvermittlungen
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
